/**
 * Situation rules — the authored, deterministic mapping from a declared
 * situation vector to a method preference.
 *
 * This is the facilitator's judgment written down: same decision shape,
 * different method, because of what they read in the room. It is a typed
 * table, reviewable in a diff, with a written reason and a provenance line per
 * rule. It is authored, never learned.
 *
 * Hard constraints, enforced by `situationRecommendation.ts` and its tests:
 *
 *   - Rules may only REORDER or ANNOTATE within the compatible set. Precedence
 *     is `readiness / compatibility (hard) > situation rules (advisory) > shape
 *     matrix (default)`. A rule can never surface a tool the readiness
 *     evaluator has blocked, and never suppresses a compatibility warning.
 *   - Rules never select silently. Every promotion and every discouragement
 *     renders its text next to the recommendation.
 *   - Rules are always overridable, and an override is recorded with the rule
 *     it overrode (`method_recommendation_overrides`) — which is how this table
 *     earns its revisions.
 *
 * Tool ids are in the shape-matrix / `methodRegistry` `toolId` space
 * (`'dot-voting'`, `'speed-boat'`, …), the same ids the recommendation
 * surfaces already consume.
 *
 * Not encoded (no declared input yet, open question for Harry): "analysis
 * paralysis → Magic Prioritization or Fast vs Right". The four questions do not
 * ask about it, and inferring it from behaviour is exactly what this layer
 * refuses to do.
 *
 * Source: docs/9-exploration/PRD-method-recommendation.md §3.3 (the facilitator
 * table) and §5 Phase 3.
 */

import type { DecisionClass } from '../types/prioritization/session';
import type { SituationVector } from '../types/prioritization/situationVector';

export interface SituationDiscouragement {
  toolId: string;
  /** Shown verbatim beside the tool. */
  because: string;
}

export interface SituationEscalation {
  /** The decision class to run first — the disagreement is one level up. */
  toDecisionClass: DecisionClass;
  /** Shown verbatim. */
  because: string;
}

export interface SituationRule {
  id: string;
  /** Which situation facts trigger this rule. All must match. */
  when: Partial<SituationVector>;
  /** Tool ids this rule promotes, in preference order. */
  prefer: string[];
  /** Tool ids this rule argues against, with a reason shown to the user. */
  discourage: SituationDiscouragement[];
  /** Optional: this situation calls for a different session first. */
  escalate?: SituationEscalation;
  /** Shown verbatim in the UI. Never paraphrased, never hidden. */
  rationale: string;
  /** Provenance: which part of the methodology this encodes. */
  source: string;
}

export const SITUATION_RULES: readonly SituationRule[] = [
  {
    id: 'unheard-give-everyone-spend',
    when: { voice: 'anchored', candour: 'guarded' },
    prefer: ['buy-a-feature'],
    discourage: [],
    rationale:
      'One voice anchors the room and people will not push back on the record, so the rest of the group is going unheard. Buy a Feature gives everyone the same budget to spend: the tradeoff becomes visceral rather than argued, and nobody has to argue against the anchor voice to be counted.',
    source: 'PRD-method-recommendation §3.3, "stakeholders feel unheard"; Methods.md via methodRegistry buy_a_feature_quant',
  },
  {
    id: 'voice-anchored-hide-responses',
    when: { voice: 'anchored' },
    prefer: ['dot-voting', 'pairwise-ranking'],
    discourage: [
      {
        toolId: 'eisenhower-matrix',
        because:
          'Cards are placed in the open, so the first voice frames every quadrant before anyone else has weighed in.',
      },
      {
        toolId: 'moscow-method',
        because:
          'Bucketing happens out loud. Once the anchor voice calls something a Must, the room rarely moves it.',
      },
    ],
    rationale:
      'One voice will set the frame before others speak. Dot Voting and Pairwise collect each response privately and reveal them together at close, so a single voice cannot anchor the result.',
    source: 'PRD-method-recommendation §3.3, "one senior voice anchors the group"; Methods.md via methodRegistry dot_voting_qual, pairwise_quant',
  },
  {
    id: 'candour-guarded-give-cover',
    when: { candour: 'guarded' },
    prefer: ['speed-boat', 'dot-voting'],
    discourage: [
      {
        toolId: 'stack-ranking',
        because:
          'A public forced order asks each person to defend a position on the record. When candour is low, the order reflects what felt safe to say, not what people think.',
      },
    ],
    rationale:
      'People will not reliably say the real thing on the record. Speed Boat names anchors and forces through a metaphor, which gives cover to raise problems no one will state directly. Dot Voting keeps individual responses private until close.',
    source: 'PRD-method-recommendation §3.3, "low trust"; Methods.md via methodRegistry speedboat_qual ("obstacle/force surfacing and clustering")',
  },
  {
    id: 'stability-reversing-lock-the-order',
    when: { stability: 'reversing' },
    prefer: ['stack-ranking', 'simple-weighted-scoring'],
    discourage: [
      {
        toolId: 'dot-voting',
        because:
          'A vote count gives the next reversal nothing to argue against. It records a preference, not a reason.',
      },
      {
        toolId: 'magic-prioritization',
        because:
          'A rapid pass produces an order without a record of why. A group that reverses itself needs the why.',
      },
    ],
    rationale:
      'This group has reversed a decision of this kind recently. Forced ranking against locked criteria, or weighted scoring with the weights on the record, produces a strict order with a written basis there is something to point back at.',
    source: 'PRD-method-recommendation §3.3, "the team keeps reversing itself"; Methods.md via methodRegistry stack_ranking_quant ("pure forced linear ordering")',
  },
  {
    id: 'altitude-contested-go-up-a-level',
    when: { altitude: 'criteria_contested' },
    prefer: [],
    discourage: [
      {
        toolId: 'simple-weighted-scoring',
        because: 'Weights encode an agreement about what matters that this room has not reached.',
      },
      {
        toolId: 'rice-scoring',
        because: 'RICE fixes the criteria in advance. The disagreement here is about the criteria.',
      },
      {
        toolId: 'wsjf',
        because: 'WSJF fixes the criteria in advance. The disagreement here is about the criteria.',
      },
    ],
    escalate: {
      toDecisionClass: 'S3_ATTRIBUTE_SELECTION',
      because:
        'The disagreement is one level up. Ranking items will not resolve a dispute about what matters. Run criteria selection first.',
    },
    rationale:
      'You disagree on what matters, not on the items. Stop: run a criteria selection session first. Ranking items against contested criteria produces an order nobody accepts.',
    source: 'PRD-method-recommendation §3.3, "they can\'t agree on what matters"; Doc 02 §2A (criteria are a Stage 3 decision)',
  },
];

/** Number of declared facts a rule requires. More specific rules fire first. */
export function ruleSpecificity(rule: SituationRule): number {
  return Object.values(rule.when).filter((v) => v !== undefined).length;
}

/** All facts in `rule.when` are declared in `vector` with the same value. */
export function ruleMatches(rule: SituationRule, vector: SituationVector | null | undefined): boolean {
  if (!vector) return false;
  const facts = Object.entries(rule.when).filter(([, v]) => v !== undefined);
  if (facts.length === 0) return false;
  return facts.every(([key, value]) => vector[key as keyof SituationVector] === value);
}
